import httpService from "../../../../services/httpService";
import type { TimelineEntry } from "../../../../shared/components/Timeline";

export interface JobApplication {
  application_id?: string;
  company_id?: string | null;
  opportunity_id?: string | null;
  opportunity_title?: string | null;
  candidate_id?: string | null;
  candidate_email?: string | null;
  candidate_name?: string | null;
  resume_s3_key?: string | null;
  resume_url?: string | null;
  resume_name?: string | null;
  source?: string | null;
  work_location?: string | null;
  work_type?: string | null;
  job_type?: string | null;
  employment_type?: string | null;
  stage: string;
  match_score?: number | null;
  match_breakdown?: any;
  match_status?: string;
  rejected?: boolean;
  rejection_reason_type?: string | null;
  rejection_reason_text?: string | null;
  rejected_at?: string | null;
  candidate_photo_url?: string | null;
  candidate_skills?: string[] | null;
  candidate_experience_years?: number | null;
  candidate_education?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ApplicationStageHistoryEntry {
  stage: string;
  previous_stage?: string | null;
  changed_at: string;
  changed_by?: string | null;
  duration_in_previous_stage?: string | null;
  notes?: string | null;
  reason_type?: string | null;
  reason_text?: string | null;
}

export interface ApplicationTimelineResponse {
  opportunity: any;
  application: any;
  timeline: ApplicationStageHistoryEntry[];
  entries?: TimelineEntry[];
}

export interface JobApplicationListView {
  total_no_of_records: number;
  no_of_records: number;
  records: JobApplication[];
}

export interface ApplicationMatch {
  application_id: string;
  match_status: string;
  match_score?: number | null;
  match_breakdown?: any;
}

class JobApplicationClient {
  private basePath = "/job_opportunities";

  async getStages(opportunityId: string): Promise<string[]> {
    const response = await httpService.get<string[]>(`${this.basePath}/${opportunityId}/stages`);
    return response.data;
  }

  async getRejectionReasons(): Promise<string[]> {
    const response = await httpService.get<string[]>(`${this.basePath}/rejection_reasons`);
    return response.data;
  }

  async getApplicants(opportunityId: string): Promise<JobApplicationListView> {
    const response = await httpService.get<JobApplicationListView>(
      `${this.basePath}/${opportunityId}/applicants`
    );
    return response.data;
  }

  async addApplicant(
    opportunityId: string,
    formData: FormData
  ): Promise<JobApplication> {
    const response = await httpService.post<JobApplication>(
      `${this.basePath}/${opportunityId}/applicants`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  }

  async moveStage(
    opportunityId: string,
    candidateId: string,
    stage: string
  ): Promise<JobApplication> {
    const response = await httpService.post<JobApplication>(
      `${this.basePath}/${opportunityId}/applicants/${candidateId}/stage`,
      { stage }
    );
    return response.data;
  }

  async bulkMoveStage(
    opportunityId: string,
    candidateIds: string[],
    stage: string,
    reasonType?: string,
    customReason?: string
  ): Promise<any> {
    const body: any = { candidate_ids: candidateIds, stage };
    if (reasonType) body.reason_type = reasonType;
    if (customReason) body.custom_reason = customReason;

    const response = await httpService.post<any>(
      `${this.basePath}/${opportunityId}/applicants/bulk_stage`,
      body
    );
    return response.data;
  }
  async rejectApplicant(
    opportunityId: string,
    candidateId: string,
    reasonType: string,
    customReason?: string
  ): Promise<JobApplication> {
    const body: { reason_type: string; custom_reason?: string } = {
      reason_type: reasonType,
    };
    if (customReason) body.custom_reason = customReason;
    const response = await httpService.post<JobApplication>(
      `${this.basePath}/${opportunityId}/applicants/${candidateId}/reject`,
      body
    );
    return response.data;
  }

  async declineApplicant(
    opportunityId: string,
    candidateId: string,
    reasonType: string,
    customReason?: string
  ): Promise<JobApplication> {
    const body: { reason_type: string; custom_reason?: string } = {
      reason_type: reasonType,
    };
    if (customReason) body.custom_reason = customReason;
    const response = await httpService.post<JobApplication>(
      `${this.basePath}/${opportunityId}/applicants/${candidateId}/decline`,
      body
    );
    return response.data;
  }

  async getMatch(
    opportunityId: string,
    candidateId: string
  ): Promise<ApplicationMatch> {
    const response = await httpService.get<ApplicationMatch>(
      `${this.basePath}/${opportunityId}/applicants/${candidateId}/match`
    );
    return response.data;
  }

  async getCandidateApplications(
    candidateId: string
  ): Promise<JobApplicationListView> {
    const response = await httpService.get<any>(
      `${this.basePath}/candidates/${candidateId}/applications`
    );
    if (Array.isArray(response.data)) {
      return { total_no_of_records: response.data.length, no_of_records: response.data.length, records: response.data };
    }
    return response.data || { total_no_of_records: 0, no_of_records: 0, records: [] };
  }

  async getApplicationTimeline(
    opportunityId: string,
    candidateId: string
  ): Promise<ApplicationTimelineResponse> {
    const response = await httpService.get<any>(
      `${this.basePath}/${opportunityId}/applicants/${candidateId}/timeline`
    );
    const data = response.data || {};
    const rawTimeline: ApplicationStageHistoryEntry[] = data.timeline || (Array.isArray(data) ? data : []);
    const entries: TimelineEntry[] = rawTimeline.map((item) => {
      let badgeAccent: "green" | "amber" | "red" | "neutral" = "neutral";
      const s = (item.stage || "").toLowerCase();
      if (s === "offered" || s === "accepted" || s === "hired") badgeAccent = "green";
      else if (s === "rejected" || s === "declined") badgeAccent = "red";
      else if (s === "interviewed" || s === "shortlisted") badgeAccent = "amber";

      const bullets: string[] = [];
      if (item.notes) bullets.push(item.notes);
      if (item.reason_type) bullets.push(`Reason: ${item.reason_type}`);
      if (item.reason_text && item.reason_text !== item.notes) bullets.push(item.reason_text);

      let dateRange = item.changed_at;
      if (item.duration_in_previous_stage) {
        dateRange += ` · Stayed in stage: ${item.duration_in_previous_stage}`;
      }

      return {
        title: item.previous_stage ? `Moved from ${item.previous_stage} to ${item.stage}` : `Stage: ${item.stage}`,
        subtitle: item.changed_by ? `Changed by: ${item.changed_by}` : undefined,
        dateRange,
        badge: item.stage,
        badgeAccent,
        bullets: bullets.length > 0 ? bullets : undefined,
      };
    });

    return {
      opportunity: data.opportunity || null,
      application: data.application || null,
      timeline: rawTimeline,
      entries,
    };
  }
}

const jobApplicationClient = new JobApplicationClient();
export default jobApplicationClient;
